import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Building2, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
    meta.content = 'noindex, follow';
    return () => { if (meta) meta.content = 'index, follow'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success('Erfolgreich angemeldet');
      navigate('/admin');
    } catch {
      toast.error('Ungültige Anmeldedaten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-6">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-semibold text-lg tracking-tight">VerwalterHonorar.de</span>
        </div>
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="mx-auto h-10 w-10 rounded-full bg-secondary flex items-center justify-center mb-2">
              <Lock className="h-4 w-4 text-muted-foreground" />
            </div>
            <CardTitle className="text-lg">Admin-Anmeldung</CardTitle>
            <CardDescription>Melden Sie sich an, um auf den Verwaltungsbereich zuzugreifen.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-user">Benutzername oder E-Mail</Label>
                <Input id="login-user" value={username} onChange={e => setUsername(e.target.value)} placeholder="admin oder name@firma.de" autoFocus data-testid="login-username" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-pass">Passwort</Label>
                <Input id="login-pass" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Passwort" data-testid="login-password" />
              </div>
              <Button type="submit" className="w-full" disabled={loading || !username || !password} data-testid="login-submit-btn">
                {loading ? 'Anmelden...' : 'Anmelden'}
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Noch kein Konto? <Link to="/registrieren" className="text-primary hover:underline font-medium" data-testid="goto-register-link">Registrieren</Link>
              <span className="mx-2 text-border">|</span>
              <Link to="/passwort-vergessen" className="text-primary hover:underline font-medium" data-testid="forgot-password-link">Passwort vergessen?</Link>
            </p>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-muted-foreground mt-6">Scholze-Kurz & Kurz Immobilien GmbH</p>
        <p className="text-center text-xs text-muted-foreground/50 mt-1" data-testid="app-version">v{process.env.REACT_APP_BUILD_NUMBER || '1'}</p>
      </div>
    </div>
  );
}
